import Flex from "@ui-kit/layout/Flex/Flex.web";
import { FormProvider } from "react-hook-form";

import React, { useCallback, useEffect } from "react";
import { GuestQuestionType } from "@shared/modules/basket/types/common/serializer-types";
import ParsedHtml from "@shared/components/ui-kit/typography/ParsedHtml/ParsedHtml.web";
import { GuestQuestionsFormProps } from "./GuestQuestionsForm.props";
import GuestQuestion from "../GuestQuestion/GuestQuestion.web";

export default function GuestQuestionsForm({
  formId,
  form,
  guestQuestions,
  updateGuestQuestions,
  initialValuesData,
}: GuestQuestionsFormProps) {
  useEffect(() => {
    if (initialValuesData) {
      Object.keys(initialValuesData).forEach((key) => {
        form.setValue(key, initialValuesData[key]);
      });
    }
  }, [initialValuesData]);

  // for multiguest itemgroups, all responses are stored on the same form object
  // we need to filter out the current itemgroup's responses when updating any input field
  const getFormResponses = useCallback(() => {
    const allFormValues = form.getValues();
    const currentFormKeys = Object.keys(form.getValues()).filter((key) =>
      key.includes(formId)
    );

    return currentFormKeys.map((key) => allFormValues[key]);
  }, [form, formId]);

  // Logic that should run every time an input field is changed
  const onUpdateQuestion = useCallback(
    (formInputId: string) => {
      const formResponses = getFormResponses();

      // update responses for the entire guestQuestions array
      // because all itemGroups are modified at once for guest questions
      // (use the form values as the source of truth)
      const updatedQuestions = guestQuestions.map((question, index) => {
        return {
          ...question,
          response: formResponses[index] ?? null,
        };
      });

      // trigger form validation for the updated input
      form.trigger(formInputId);

      // update the questions array
      updateGuestQuestions?.(updatedQuestions);
    },
    [getFormResponses, guestQuestions, form]
  );

  return (
    <FormProvider {...form}>
      <form data-testid={formId} id={formId}>
        <Flex direction="column" gap="lg">
          {guestQuestions?.map(
            (questionObject: GuestQuestionType, questionIndex: number) => (
              <React.Fragment key={questionObject.title}>
                {questionObject.custom_html && (
                  <ParsedHtml htmlString={questionObject.custom_html} />
                )}
                <GuestQuestion
                  formId={formId}
                  questionIndex={questionIndex}
                  onUpdateQuestion={onUpdateQuestion}
                  {...questionObject}
                />
              </React.Fragment>
            )
          )}
        </Flex>
      </form>
    </FormProvider>
  );
}
